import { inject, Injectable } from '@angular/core';

import { WithAuditContext } from '../http-mutating-operations.interceptor';
import {
  RealtimeConnection,
  RealtimeEventHandler,
  RealtimeMessage,
  RealtimeService,
} from '../realtime.service';
import { ListResult } from '../semantria-api.service';
import { StorageAPIService } from '../storage-api.service';

export type AiInsightStatus = 'QUEUED' | 'IN_PROGRESS' | 'FAILED' | 'COMPLETED';
type AiInsightFormat = 'Text' | 'JSON' | 'Markdown';

export interface AiInsightProperties {
  accountId?: string;
  projectId?: string;
  userId?: string;
  dashboardId?: string;
  widgetId?: string;
  sceneId?: string;
  summary?: string;
  completionTaskId?: string;
  longPurpose?: string;
  shortPurpose?: string;
  includeImage?: boolean;
  analysisId?: string;
}

export interface AiInsightLocalization {
  insight_localization_id: string;
  language: string;
  text: string;
  creator_user_id: string;
  creation_date: string;
}

export interface AiInsight {
  insight_id: string;
  insight_status: AiInsightStatus;
  insight_format: AiInsightFormat;
  group_id: string;
  product: string;
  error_message?: string;
  scene_scenario_id?: string;
  scene_scenario_version?: number;
  evaluation_id?: string;
  job_id?: string;
  batch_id?: string;
  batch_item_id?: string;
  original_language: string;
  location_ids?: string;
  properties: AiInsightProperties;
  localizations?: AiInsightLocalization[];
  creator_user_id: string;
  creation_date: string;
  modifier_user_id: string;
  modification_date: string;
  publisher_user_id?: string;
  published?: boolean;
  tags?: string[];
  comment?: string;
  images?: AiInsightImage[];
}

export interface AiInsightImage {
  image_id: string;
  filename: string;
  content_type: string;
  size: number;
}

@Injectable({
  providedIn: 'root',
})
export class AiInsightsService extends StorageAPIService {
  private realtimeService = inject(RealtimeService);

  on(
    namespace: string,
    fn: RealtimeEventHandler<{ id: string; widgetId: string }>
  ): RealtimeConnection {
    return this.realtimeService.on(
      namespace,
      {
        aiInsight: (data: RealtimeMessage<{ id: string; widgetId: string }>) => {
          fn(data);
        },
      },
      'aiInsightsService'
    );
  }

  getInsights(searchParams: AiInsightProperties) {
    return this.get<ListResult<AiInsight>>('/insights/', {
      page_size: 1024,
      sort: 'modification_date',
      reverse_order: true,
      product: 'SPOTLIGHT',
      properties: Object.entries(searchParams)
        .filter(([_, value]) => value != null && value !== '')
        .map(([key, value]) => `${key}:${value}`),
    });
  }

  getInsight(id: string) {
    return this.get<AiInsight>(`/insights/${id}/`);
  }

  updateInsight(insightId: string, params: Partial<AiInsight>) {
    return this.patch<AiInsight>(`/insights/${insightId}`, params);
  }

  addInsightLocalization(insightId: string, params: Partial<AiInsightLocalization>) {
    return this.put<AiInsightLocalization>(`/insights/${insightId}/languages/`, params);
  }

  deleteInsightLocalization(insightId: string, localizationId: string) {
    return this.delete(`/insights/${insightId}/languages/${localizationId}/`);
  }

  getInsightTags(searchParams: AiInsightProperties) {
    return this.get<string[]>('/insight-tags/', {
      page_size: 1024,
      sort: 'modification_date',
      reverse_order: true,
      product: 'SPOTLIGHT',
      properties: Object.entries(searchParams)
        .filter(([_, value]) => value != null && value !== '')
        .map(([key, value]) => `${key}:${value}`),
    });
  }

  @WithAuditContext('insight-deleted', (_, args) => {
    return { id: args[0] };
  })
  deleteInsight(id: string): Promise<string> {
    return this.delete(`/insights/${id}/`)
      .then(() => id)
      .catch(() => id);
  }

  uploadImage(id: string, file: File) {
    const formData = new FormData();
    formData.append('file', file);
    return this.post<AiInsightImage>(`/insights/${id}/images/`, formData);
  }

  getImages(id: string) {
    return this.get<AiInsightImage[]>(`/insights/${id}/images/`);
  }

  getImage(insightId: string, imageId: string) {
    return this.get<AiInsightImage>(`/insights/${insightId}/images/${imageId}/`);
  }

  downloadImage(insightId: string, imageId: string) {
    return this.getBinary(`/insights/${insightId}/images/${imageId}/download`);
  }

  deleteImage(insightId: string, imageId: string) {
    return this.delete(`/insights/${insightId}/images/${imageId}/`);
  }
}
